import produce from "immer"
import {
  getCurrentItemOrderIds,
  getNextActiveIndex,
  getNextItemOrderIds,
  getNextOrder,
  maybeApplyAutoAdd,
  updateOrdering
} from "./utils"

export const types = {
  ADD_ITEM: "ADD_ITEM",
  REMOVE_ITEM: "REMOVE_ITEM",
  UPDATE_ITEM: "UPDATE_ITEM",
  UPDATE_ITEM_ORDER: "UPDATE_ITEM_ORDER",
  SYNC_ITEMS: "SYNC_ITEMS",
  SET_ACTIVE_INDEX: "SET_ACTIVE_INDEX"
}

const handleChange = (payload, state) => {
  const { oldIndex, newIndex, changeType, updateIndex } = payload

  const items = updateOrdering(maybeApplyAutoAdd(payload.items, state))

  return {
    itemOrderIds: getNextItemOrderIds(
      getCurrentItemOrderIds(items, state.itemOrderIds || []),
      oldIndex,
      newIndex
    ),
    activeIndex:
      updateIndex ||
      getNextActiveIndex(
        changeType,
        newIndex,
        oldIndex,
        items,
        state.activeIndex
      ),
    items
  }
}

export const add = ({ changeType }, state) => {
  const { items, generateItem, autoGenerateItem } = state

  if (!generateItem) {
    console.error("generateItem function is required to add items to a List")
    return state
  }

  const nextOrder = getNextOrder(items)
  const nextItem = generateItem({ order: nextOrder })
  const nextItems = produce(items, (draft) => {
    draft.push(nextItem)
  })

  // "autoGenerateItem" always adds a "new" (blank) item that shouldn't be counted
  const realItemsLength = autoGenerateItem
    ? (items.length || 1) - 1
    : items.length

  // calc the new item's index
  const newIndex = realItemsLength

  return {
    ...handleChange({ items: nextItems, newIndex, changeType }, state)
  }
}

export const remove = ({ index, changeType }, state) => {
  const { autoGenerateItem, items, min: propsMin = 1 } = state

  // The auto-generated item doesn't count towards the minimum
  const min = autoGenerateItem ? propsMin + 1 : propsMin

  // Ensure the minimum number of items is not removed
  if (items.length <= min) return { items }

  const nextItems = items.filter((item, i) => {
    return i !== index
  })

  const oldIndex = nextItems.length !== items.length ? index : null

  return {
    ...handleChange(
      {
        items: nextItems,
        oldIndex,
        changeType
      },
      state
    )
  }
}

export const update = (
  { index, attribute, value, updateFunction, changeType },
  state
) => {
  const { items } = state

  let nextItems = items

  if (updateFunction) {
    nextItems = updateFunction({
      items,
      item: items[index],
      index
    }).items
  } else {
    nextItems = produce(items, (draft) => {
      draft[index][attribute] = value
    })
  }

  return {
    ...handleChange(
      {
        items: nextItems,
        changeType,
        updateIndex: index
      },
      state
    )
  }
}

export const updateOrder = ({ oldIndex, newIndex, items }, state) => {
  // Items have already been sorted by the DragSort component
  return {
    ...handleChange(
      {
        items,
        oldIndex,
        newIndex,
        changeType: "sort"
      },
      state
    )
  }
}

export const setActiveIndex = ({ index }) => {
  return { activeIndex: index }
}

export const syncItems = ({ items }, state) => {
  return handleChange({ items, changeType: "sync" }, state)
}
